import React from 'react';
import { connect } from 'react-redux';
import { List } from '@mui/material';
import featuresRegistry from './featuresRegistry';
import FeatureListItem from './FeatureListItem';
import { createActions } from '../../store';
import { previewHistoryBatch } from '../../store/extensions';
import useFullscreenPreview from '../../Preview/useFullscreenPreview';
import { trackEvent, AnalyticsConstants } from '../../../../../utilities/analyticsUtilities';
import { saveTriedFeatureId } from '../../../../../utilities/storageUtilities';

const FeaturesEditor = ({ videoSettings, triedFeatureIds, optimizationModeEnabled, dispatch, setProperty, setOptimizationMode }) => {
    const { isSupported: fullscreenSupported, isFullscreen: previewFullscreen, toggleFullscreen } = useFullscreenPreview();

    const editor = {
        optimizationModeEnabled,
        setOptimizationMode,
        fullscreenSupported,
        previewFullscreen,
        togglePreviewFullscreen: toggleFullscreen
    };

    const markFeatureTried = (feature) => {
        saveTriedFeatureId(feature.id);

        if (!triedFeatureIds.includes(feature.id))
            setProperty('triedFeatureIds', [...triedFeatureIds, feature.id]);
    };

    const isFeatureDisabled = (feature) => Boolean(feature.isDisabled?.(videoSettings, editor));

    const isFeatureAvailable = (feature) => feature.isAvailable?.(videoSettings, editor) ?? true;

    const toggleFeature = (feature, checked) => {
        if (isFeatureDisabled(feature))
            return;

        if (!feature.editorSetting)
            previewHistoryBatch.start();

        if (checked)
            feature.onEnable(dispatch, videoSettings, editor);
        else
            feature.onDisable(dispatch, videoSettings, editor);

        if (!feature.editorSetting)
            previewHistoryBatch.end();

        if (checked) {
            trackEvent('Enabled Feature', {
                feature: feature.id,
                category: AnalyticsConstants.VIDEO_CATEGORY
            });
            markFeatureTried(feature);
        }
    };

    const navigateToFeature = (feature) => {
        if (feature.navigation.visualizerTab !== undefined)
            setProperty('visualizerPanelRequestedTab', feature.navigation.visualizerTab);

        // The Elements panel keeps its tab in the store rather than asking for one, so the tab is
        // set outright instead of going through a one-shot request the way the Visualizer does.
        if (feature.navigation.elementsTab !== undefined)
            setProperty('elementsSelectedTab', feature.navigation.elementsTab);

        setProperty('currentSettingsPanel', feature.navigation.panel);

        trackEvent('Clicked Feature Arrow', {
            feature: feature.id,
            category: AnalyticsConstants.VIDEO_CATEGORY
        });
    };

    return (
        <List dense disablePadding id='features-editor-list'>
            {featuresRegistry.filter(isFeatureAvailable).map((feature) =>
                <FeatureListItem
                    key={feature.id}
                    feature={feature}
                    checked={Boolean(feature.isActive(videoSettings, editor))}
                    disabled={isFeatureDisabled(feature)}
                    showNewBadge={feature.isNew && !triedFeatureIds.includes(feature.id)}
                    onToggle={(checked) => toggleFeature(feature, checked)}
                    onNavigate={feature.navigation ? () => navigateToFeature(feature) : undefined}
                />
            )}
        </List>
    );
};

const mapStateToProps = (state) => {
    const { videoSettings } = state.createPreview.present;
    const { triedFeatureIds, optimizationModeEnabled } = state.create;
    return { videoSettings, triedFeatureIds, optimizationModeEnabled };
};

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const setOptimizationMode = (enabled) => dispatch(createActions.setOptimizationMode(enabled));
    return { dispatch, setProperty, setOptimizationMode };
};

export default connect(mapStateToProps, mapDispatchToProps)(FeaturesEditor);
